import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  createSurvey,
  publishDefinition,
  getErrorMessage,
  getWorkspace,
  type CreateSurveyInput,
} from '@/lib/api';
import type { SurveyDefinition, IdentitySchema } from '@webforge-survey/shared';
import { PageHeader, Icon } from '@/components/ui';
import { IdentitySchemaEditor } from './IdentitySchemaEditor';
import { schemaForTemplate } from '@/lib/identity-templates';

/**
 * Création d'une enquête (NewSurveyPage) — refonte v2 2026-05-20.
 * Route : /workspaces/:id/surveys/new
 *
 * Design :
 *  - Layout 2 colonnes (form + side info RGPD)
 *  - Radio-cards pour identityMode et startMode (clic sur la carte entière)
 *  - identityMode rappelle visuellement le verrou post-diffusion
 */
function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 64);
}

const IDENTITY_MODES = [
  {
    value: 'anonymous' as const,
    label: 'Anonyme',
    description: "Aucune donnée d'identité collectée. Le mode le plus sûr pour les sujets sensibles.",
    badge: 'RGPD-recommandé',
  },
  {
    value: 'optional' as const,
    label: 'Opt-in identité (au choix du répondant)',
    description: 'Le répondant peut accepter de fournir une adresse e-mail pour être recontacté. Stocké séparément des réponses.',
    badge: null,
  },
  {
    value: 'required' as const,
    label: 'Identifiée (obligatoire)',
    description: "Identité requise avant d'accéder au questionnaire. À réserver aux enquêtes nominatives justifiées.",
    badge: 'Sensible',
  },
];

export default function NewSurveyPage(): JSX.Element {
  const { id: workspaceId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [identityMode, setIdentityMode] =
    useState<'anonymous' | 'optional' | 'required'>('anonymous');
  const [identitySchema, setIdentitySchema] = useState<IdentitySchema>(() =>
    schemaForTemplate('minimal'),
  );
  const [retentionDays, setRetentionDays] = useState<number>(1825);
  const [startMode, setStartMode] = useState<'empty' | 'json'>('empty');
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Plafond de rétention du workspace (B8) : la rétention d'une enquête ne peut
  // pas le dépasser (le backend rejette sinon). On borne l'UI en conséquence.
  const workspaceQ = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => getWorkspace(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });
  const retentionCap = workspaceQ.data?.retentionDaysCap ?? 1825;

  // Quand le plafond est connu, on ramène la valeur par défaut sous le plafond.
  useEffect(() => {
    const cap = workspaceQ.data?.retentionDaysCap;
    if (cap !== undefined) setRetentionDays((d) => Math.min(d, cap));
  }, [workspaceQ.data]);

  if (!workspaceId) {
    return <p>Workspace introuvable.</p>;
  }

  const handleTitleChange = (v: string): void => {
    setTitle(v);
    if (!slugTouched) setSlug(slugify(v));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      let parsedDef: SurveyDefinition | null = null;
      if (startMode === 'json') {
        try {
          parsedDef = JSON.parse(jsonText) as SurveyDefinition;
        } catch {
          throw new Error('JSON invalide — vérifier la syntaxe.');
        }
        if (!parsedDef.blocks || !Array.isArray(parsedDef.blocks)) {
          throw new Error('La définition doit contenir un tableau `blocks`.');
        }
      }

      const input: CreateSurveyInput = {
        title: title.trim(),
        slug: slug.trim(),
        identityMode,
        retentionDays: Math.max(1, Math.min(retentionDays, retentionCap)),
        // identitySchema obligatoire (backend) pour optional/required ; ignoré en anonymous.
        ...(identityMode !== 'anonymous'
          ? { identitySchema: identitySchema as unknown as Record<string, unknown> }
          : {}),
      };
      const created = await createSurvey(workspaceId, input);

      if (parsedDef) {
        const definition: SurveyDefinition = {
          ...parsedDef,
          id: created.slug,
          identityMode,
        };
        await publishDefinition(workspaceId, created.id, definition);
      }

      navigate(`/workspaces/${workspaceId}/surveys/${created.id}/edit`);
    } catch (err) {
      setError(getErrorMessage(err, 'Création impossible.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Nouvelle enquête"
        subtitle="Choisissez le mode d'identification — il sera verrouillé dès la première diffusion."
        back={{ to: `/workspaces/${workspaceId}/surveys`, label: 'Retour aux enquêtes' }}
      />

      <div className="grid lg:grid-cols-[1fr,300px] gap-6 items-start">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Section 1 : Identité de l'enquête */}
          <section className="card card-flush">
            <header className="card-header">
              <div>
                <h2 className="text-base font-semibold">Identité de l'enquête</h2>
                <p className="text-xs text-text-muted mt-0.5">Titre et URL publique.</p>
              </div>
            </header>
            <div className="card-section space-y-5">
              <div className="field">
                <label htmlFor="new-title" className="field-label">Titre</label>
                <input
                  id="new-title"
                  type="text"
                  className="input-base"
                  required
                  maxLength={140}
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Ex. Coparentalité après séparation"
                />
              </div>

              <div className="field">
                <label htmlFor="new-slug" className="field-label">
                  Slug (apparaît dans l'URL publique)
                </label>
                <div className="flex items-stretch rounded-lg border border-border bg-surface overflow-hidden focus-within:border-primary">
                  <span className="px-3 inline-flex items-center text-text-muted text-sm border-r border-border bg-bg-deep/40 font-mono">
                    /s/
                  </span>
                  <input
                    id="new-slug"
                    type="text"
                    className="flex-1 px-3 py-2.5 bg-transparent text-sm font-mono outline-none placeholder:text-text-subtle"
                    required
                    maxLength={140}
                    value={slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(e.target.value);
                    }}
                    placeholder="coparentalite-2026"
                  />
                </div>
                <p className="field-hint">Auto-généré depuis le titre · modifiable.</p>
              </div>
            </div>
          </section>

          {/* Section 2 : Identification du répondant */}
          <section className="card card-flush">
            <header className="card-header">
              <div className="flex items-center gap-2">
                <Icon.Lock className="text-lock" size={16} />
                <h2 className="text-base font-semibold">Mode d'identification du répondant</h2>
              </div>
              <span className="badge badge-notice">Verrouillé après diffusion</span>
            </header>
            <fieldset className="card-section">
              <legend className="sr-only">Mode d'identification</legend>
              <p className="text-xs text-text-muted mb-4">
                Choisissez avec soin. Une fois la première diffusion ouverte, ce
                paramètre est immuable (cohérence du consentement annoncé — RGPD_PLAN §1).
              </p>
              <div className="space-y-2">
                {IDENTITY_MODES.map((mode) => {
                  const checked = identityMode === mode.value;
                  return (
                    <label
                      key={mode.value}
                      className={`option-card ${checked ? 'option-card-selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="identityMode"
                        value={mode.value}
                        checked={checked}
                        onChange={() => setIdentityMode(mode.value)}
                        className="mt-1"
                      />
                      <div className="text-sm flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <strong className="text-text">{mode.label}</strong>
                          {mode.badge && (
                            <span className="badge badge-primary">{mode.badge}</span>
                          )}
                        </div>
                        <p className="text-text-muted mt-0.5">{mode.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {identityMode !== 'anonymous' && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h3 className="text-sm font-medium mb-1">Champs d'identité collectés</h3>
                  <p className="text-xs text-text-muted mb-3">
                    Ces champs seront demandés au répondant{' '}
                    {identityMode === 'optional' ? "s'il choisit de s'identifier" : 'avant de répondre'}.
                    Ils sont chiffrés et stockés séparément des réponses.
                  </p>
                  <IdentitySchemaEditor schema={identitySchema} onChange={setIdentitySchema} />
                </div>
              )}
            </fieldset>
          </section>

          {/* Section 3 : Rétention */}
          <section className="card card-flush">
            <header className="card-header">
              <h2 className="text-base font-semibold">Durée de rétention</h2>
            </header>
            <div className="card-section">
              <label htmlFor="new-retention" className="field-label sr-only">
                Rétention (jours)
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="new-retention"
                  type="number"
                  min={1}
                  max={retentionCap}
                  className="input-base w-32 tabular-nums"
                  value={retentionDays}
                  onChange={(e) =>
                    setRetentionDays(Math.max(1, Math.min(retentionCap, Number(e.target.value) || retentionCap)))
                  }
                />
                <span className="text-sm text-text-muted">jours</span>
              </div>
              <p className="field-hint mt-2">
                Plafonné par la rétention max du workspace (<strong>{retentionCap} j</strong>).
                Une valeur supérieure est automatiquement ramenée à ce plafond.
              </p>
            </div>
          </section>

          {/* Section 4 : Point de départ */}
          <section className="card card-flush">
            <header className="card-header">
              <h2 className="text-base font-semibold">Point de départ</h2>
            </header>
            <fieldset className="card-section">
              <legend className="sr-only">Point de départ</legend>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className={`option-card ${startMode === 'empty' ? 'option-card-selected' : ''}`}>
                  <input
                    type="radio"
                    name="startMode"
                    checked={startMode === 'empty'}
                    onChange={() => setStartMode('empty')}
                    className="mt-1"
                  />
                  <div className="text-sm">
                    <strong>Démarrer vide</strong>
                    <p className="text-text-muted mt-0.5">
                      Ajouter blocs / questions via l'éditeur.
                    </p>
                  </div>
                </label>
                <label className={`option-card ${startMode === 'json' ? 'option-card-selected' : ''}`}>
                  <input
                    type="radio"
                    name="startMode"
                    checked={startMode === 'json'}
                    onChange={() => setStartMode('json')}
                    className="mt-1"
                  />
                  <div className="text-sm">
                    <strong>Importer JSON</strong>
                    <p className="text-text-muted mt-0.5">
                      Coller une définition (V16, …) — publiée à la création.
                    </p>
                  </div>
                </label>
              </div>

              {startMode === 'json' && (
                <textarea
                  className="input-base font-mono text-xs w-full h-48 mt-3"
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  placeholder='{ "id": "...", "definitionVersion": 1, "locale": "fr", "title": "...", "identityMode": "anonymous", "blocks": [] }'
                />
              )}
            </fieldset>
          </section>

          {error && (
            <p role="alert" className="field-error">
              <Icon.AlertTriangle />
              <span>{error}</span>
            </p>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/workspaces/${workspaceId}/surveys`)}
              className="btn-ghost"
            >
              Annuler
            </button>
            <button type="submit" className="btn-primary btn-lg" disabled={busy}>
              {busy ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>

        {/* Side info RGPD — sticky aside */}
        <aside className="hidden lg:block sticky top-20">
          <div className="card">
            <h3 className="text-sm font-semibold inline-flex items-center gap-2">
              <Icon.Shield size={18} className="text-primary" />
              Garanties RGPD
            </h3>
            <ul className="mt-3 space-y-3 text-xs text-text-muted">
              <li>
                <strong className="text-text">Anti-rebinding par construction</strong> — en
                mode anonyme, aucune identité n'est techniquement attachable à une réponse.
              </li>
              <li>
                <strong className="text-text">Chiffrement applicatif</strong> — toutes les
                réponses des items <code>meta.sensitive</code> sont chiffrées AES-256-GCM
                au repos.
              </li>
              <li>
                <strong className="text-text">k-anonymat</strong> — les agrégats masquent
                les modalités à effectif &lt; 5.
              </li>
              <li>
                <strong className="text-text">Hébergement UE</strong> — pas de transfert
                hors UE, pas de CDN tiers.
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </>
  );
}
